import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

import { FEATURE_FLAGS } from '~common';
import { PaddingBox } from '~components/atoms/custom';
import { PINNING_STEP, usePinningStepContext } from '~providers';
import { checkEnabledFeatureFlag } from '~utils';

enum PlacementAction {
  SWAP = 'SWAP',
  RELOCATE = 'RELOCATE',
}

const actionItemSx = (selected: PlacementAction, value: PlacementAction) => ({
  display: 'flex',
  flexDirection: 'row-reverse',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  mx: 0,
  my: 0,
  px: 2,
  py: 1.5,
  borderRadius: '100px',
  border: '1px solid',
  borderColor: selected === value ? 'primary.main' : 'rgba(0, 0, 0, 0.1)',
  '& .MuiFormControlLabel-label': {
    flex: 1,
    textAlign: 'left',
  },
});

export const SelectActionStep: React.FC = () => {
  const isRelocationEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.WEB_PINNING_RELOCATE_STATION,
  );

  const { handleSetPinningStep, setIsRelocationFlow, isRelocationFlow } =
    usePinningStepContext();

  const [selectedAction, setSelectedAction] = useState<PlacementAction>(
    isRelocationFlow ? PlacementAction.RELOCATE : PlacementAction.SWAP,
  );

  const handleContinue = useCallback(() => {
    const isRelocate = selectedAction === PlacementAction.RELOCATE;
    setIsRelocationFlow(isRelocate);
    if (isRelocate) {
      handleSetPinningStep(PINNING_STEP.CONFIRM_RELOCATION);
    } else {
      handleSetPinningStep(PINNING_STEP.ASSIGN_STATION_SCAN_QR);
    }
  }, [handleSetPinningStep, selectedAction, setIsRelocationFlow]);

  return (
    <>
      <PaddingBox
        flex={1}
        sx={{ bgcolor: 'background.paper', width: '100%', pt: 0, px: 0 }}
      >
        <Stack gap={3} pb={4} width="100%">
          <Typography variant="h6" fontWeight={600}>
            What would you like to do with this placement
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={selectedAction}
              onChange={(e) =>
                setSelectedAction(e.target.value as PlacementAction)
              }
              name="placement-action"
              sx={{ gap: 1.5 }}
            >
              <FormControlLabel
                value={PlacementAction.SWAP}
                control={
                  <Radio
                    disableRipple
                    sx={{
                      padding: 0,
                      '&:hover': { backgroundColor: 'transparent' },
                    }}
                  />
                }
                label={<Typography variant="body2">Swap Station</Typography>}
                sx={actionItemSx(selectedAction, PlacementAction.SWAP)}
              />

              {isRelocationEnabled && (
                <FormControlLabel
                  value={PlacementAction.RELOCATE}
                  control={
                    <Radio
                      disableRipple
                      sx={{
                        padding: 0,
                        '&:hover': { backgroundColor: 'transparent' },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">Relocate Placement</Typography>
                  }
                  sx={actionItemSx(selectedAction, PlacementAction.RELOCATE)}
                />
              )}
            </RadioGroup>
          </FormControl>
        </Stack>
      </PaddingBox>

      <PaddingBox
        bgcolor="background.paper"
        alignItems="center"
        position="fixed"
        sx={{ bottom: 0, left: 0 }}
        width="100%"
      >
        <Box width="100%">
          <Button
            fullWidth
            size="large"
            variant="contained"
            style={{ borderRadius: '100px' }}
            onClick={handleContinue}
          >
            <Typography variant="body2" fontWeight={500}>
              Continue
            </Typography>
          </Button>
        </Box>
      </PaddingBox>
    </>
  );
};
